package ai.jessie.web.onboarding

import ai.jessie.shared.onboarding.EntryDestination
import ai.jessie.shared.onboarding.EntryResponse
import ai.jessie.shared.onboarding.OrganisationChoiceKind
import ai.jessie.web.theme.AlertStyle
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.ChoiceCardStyle
import ai.jessie.web.theme.FieldInputStyle
import ai.jessie.web.theme.FieldStyle
import ai.jessie.web.theme.FieldsetStyle
import ai.jessie.web.theme.FormStackStyle
import ai.jessie.web.theme.HintStyle
import ai.jessie.web.theme.MutedStyle
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.StatusStyle
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.forId
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Fieldset
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Legend
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

@Composable
fun onboardingForm(state: OnboardingUiState, actions: OnboardingActions) {
    onboardingAlert(state.error, state.busy)
    if (state.error?.authenticationRequired == true) return
    if (state.cancelled) {
        P {
            Text(
                "Einrichtung pausiert. Du bleibst angemeldet. Organisationsdaten sind erst nach Abschluss verfügbar."
            )
        }
        P { actionButton("Einrichtung fortsetzen", false, actions.resume) }
        return
    }
    if (state.busy) busyStatus("Wird geladen oder gespeichert …")
    state.context?.let { context ->
        if (context.complete) {
            P {
                Text("Deine Einrichtung ist abgeschlossen. Dein Organisationszugriff wird geprüft.")
            }
        } else {
            P(attrs = HintStyle.toAttrs()) {
                Text("Bestätigte E-Mail-Adresse: ${context.verifiedEmail}")
            }
            Form(
                attrs =
                    FormStackStyle.toAttrs {
                        addEventListener("submit") {
                            it.preventDefault()
                            actions.submit()
                        }
                    }
            ) {
                profileFields(state, actions)
                organisationChoices(state, actions)
                P {
                    themedButton(
                        "Einrichtung abschliessen",
                        {},
                        ButtonVariant.PRIMARY,
                        disabled = state.busy,
                        submit = true,
                    )
                }
            }
        }
    }
    // UC-026 approved B6: the verified-email action is available before onboarding completes.
    P { A(href = "/profile") { Text("Bestätigte Anmelde-E-Mail ändern") } }
    Div(attrs = RowStyle.toAttrs()) {
        actionButton("Auswahl aktualisieren", state.busy, actions.refresh)
        if (state.context?.complete == false) actionButton("Abbrechen", state.busy, actions.cancel)
    }
}

@Composable
private fun profileFields(state: OnboardingUiState, actions: OnboardingActions) {
    Fieldset(
        attrs =
            listOf(FieldsetStyle, FormStackStyle).toAttrs { if (state.busy) attr("disabled", "") }
    ) {
        Legend { Text("Dein Profil") }
        P(attrs = MutedStyle.toAttrs()) {
            Text(
                "Vor- und Nachname: jeweils 1–250 Zeichen. " +
                    "Deine bestätigte E-Mail-Adresse bleibt unverändert."
            )
        }
        nameInput(
            "firstName",
            "Vorname",
            state.firstName,
            state.error,
            "given-name",
            actions.firstName,
        )
        nameInput(
            "lastName",
            "Nachname",
            state.lastName,
            state.error,
            "family-name",
            actions.lastName,
        )
    }
}

@Composable
private fun organisationChoices(state: OnboardingUiState, actions: OnboardingActions) {
    Fieldset(
        attrs =
            listOf(FieldsetStyle, FormStackStyle).toAttrs { if (state.busy) attr("disabled", "") }
    ) {
        Legend { Text("Organisation") }
        state.context?.choices?.forEachIndexed { index, choice ->
            choiceRadio(
                "organisation-$index",
                state.choice == choice && !state.newOrganisation,
                {
                    actions.choose(choice)
                },
            ) {
                Text("${choice.name} · ${roleLabel(choice.role)}")
                Text(
                    if (choice.kind == OrganisationChoiceKind.INVITATION) " — Einladung annehmen"
                    else " — Mitgliedschaft verwenden"
                )
            }
        }
        choiceRadio("new-organisation", state.newOrganisation, { actions.choose(null) }) {
            Text("Neue Organisation gründen")
        }
        if (state.newOrganisation) {
            nameInput(
                "organisationName",
                "Organisationsname",
                state.organisationName,
                state.error,
                "organization",
                actions.organisationName,
            )
            P(attrs = MutedStyle.toAttrs()) {
                Text(
                    "1–200 Zeichen. Du wirst Eigentümer. Geschäftseinstellungen werden später ergänzt."
                )
            }
        }
    }
}

@Composable
private fun choiceRadio(
    id: String,
    selected: Boolean,
    choose: () -> Unit,
    label: @Composable () -> Unit,
) {
    Div(attrs = ChoiceCardStyle.toAttrs()) {
        Input(
            InputType.Radio,
            attrs = {
                id(id)
                attr("name", "organisation-choice")
                checked(selected)
                onChange { choose() }
            },
        )
        Label(attrs = { forId(id) }) { label() }
    }
}

@Composable
private fun nameInput(
    id: String,
    label: String,
    value: String,
    problem: OnboardingProblem?,
    autocomplete: String,
    change: (String) -> Unit,
) {
    P(attrs = FieldStyle.toAttrs()) {
        Label(attrs = { forId(id) }) { Text(label) }
        Input(
            InputType.Text,
            attrs =
                FieldInputStyle.toAttrs {
                    id(id)
                    attr("name", id)
                    value(value)
                    attr("required", "")
                    attr("autocomplete", autocomplete)
                    if (id in problem?.fields.orEmpty()) {
                        attr("aria-invalid", "true")
                        attr("aria-describedby", "onboarding-error")
                    }
                    onInput { change(it.value) }
                },
        )
    }
}

@Composable
fun actionButton(label: String, disabled: Boolean, action: () -> Unit, elementId: String? = null) {
    themedButton(label, action, disabled = disabled, elementId = elementId)
}

/**
 * The polite progress line while a request runs; it takes the focus that disabled controls drop.
 */
@Composable
fun busyStatus(text: String) {
    P(
        attrs =
            StatusStyle.toAttrs {
                attr("role", "status")
                attr("tabindex", "-1")
                ref { line ->
                    val active = document.activeElement
                    val dropped =
                        active == null || active == document.body || active.matches(":disabled")
                    if (dropped && !AppGlobals.isExporting) line.focus()
                    onDispose {}
                }
            }
    ) {
        Text(text)
    }
}

/** [onboardingProblem] in the alert colours; renders nothing without a problem. */
@Composable
fun onboardingAlert(problem: OnboardingProblem?, busy: Boolean) {
    problem?.let { Div(attrs = AlertStyle.toAttrs()) { onboardingProblem(it, busy) } }
}

@Composable
fun onboardingProblem(problem: OnboardingProblem?, busy: Boolean) {
    problem?.let {
        P(
            attrs = {
                id("onboarding-error")
                attr("role", "alert")
                attr("tabindex", "-1")
            }
        ) {
            Text(it.message)
        }
        it.destination?.let { path ->
            P {
                A(href = path) {
                    Text(
                        it.destinationLabel
                            ?: if (it.authenticationRequired) "Anmelden" else "Weiter"
                    )
                }
            }
        }
    }
    LaunchedEffect(problem, busy) {
        if (problem != null && !busy && !AppGlobals.isExporting) {
            val target = problem.fields.firstOrNull() ?: "onboarding-error"
            (document.getElementById(target) as? HTMLElement)?.focus()
        }
    }
}

// The organisation page resolves the current role before opening Calendar or Documents.
@Composable
fun followEntry(
    entry: EntryResponse?,
    organisationPage: (String) -> String = { "/organisations/$it?home=true" },
) {
    val ctx = rememberPageContext()
    LaunchedEffect(entry) {
        if (AppGlobals.isExporting || entry == null) return@LaunchedEffect
        val path =
            when (entry.destination) {
                EntryDestination.ONBOARDING -> "/onboarding"
                EntryDestination.ORGANISATION -> organisationPage(entry.organisationId.orEmpty())
                EntryDestination.SELECT_ORGANISATION,
                EntryDestination.NO_MEMBERSHIP -> "/organisations"
            }
        if (ctx.route.path.removeSuffix("/") != path)
            ctx.router.navigateTo(path, UpdateHistoryMode.REPLACE)
    }
}

fun roleLabel(role: String) =
    when (role) {
        "OWNER" -> "Eigentümer"
        "STANDARD" -> "Mitglied"
        "ACCOUNTANT" -> "Buchhaltung"
        else -> "Unbekannte Rolle"
    }
